// src/components/PackageCard.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Star, 
  Clock, 
  TrendingUp, 
  Shield, 
  Zap,
  ArrowRight,
  Check,
  Crown,
  Target,
  DollarSign,
  Calendar,
  Building,
  Users,
  Award,
  Sparkles
} from 'lucide-react'

const PackageCard = ({ pkg, index = 0 }) => {
  const getPackageColor = (level) => {
    switch (level) {
      case 'Level 5': return 'from-[#F5AD18] to-[#9E1C60]'
      case 'Level 4': return 'from-[#9E1C60] to-[#811844]'
      case 'Level 3': return 'from-[#811844] to-[#561530]'
      case 'Level 2': return 'from-[#561530] to-[#9E1C60]'
      case 'Starter': return 'from-[#561530] to-[#811844]'
      default: return 'from-[#561530] to-[#811844]'
    }
  }

  const getPackageIcon = (level) => {
    switch (level) {
      case 'Level 5': return Award
      case 'Level 4': return Crown
      case 'Level 3': return Building
      case 'Level 2': return TrendingUp
      case 'Starter': return Star
      default: return Target
    }
  }

  const getLevelBadge = (level) => {
    const badges = {
      'Level 5': { text: 'Elite', color: 'bg-[#F5AD18] text-[#561530]' },
      'Level 4': { text: 'Premium', color: 'bg-[#9E1C60] text-white' },
      'Level 3': { text: 'Professional', color: 'bg-[#811844] text-white' },
      'Level 2': { text: 'Growth', color: 'bg-[#561530] text-white' },
      'Starter': { text: 'Foundation', color: 'bg-[#561530] text-white' }
    }
    return badges[level] || { text: level, color: 'bg-gray-500 text-white' }
  }

  const Icon = getPackageIcon(pkg.level)
  const levelBadge = getLevelBadge(pkg.level)

  const features = pkg.features || [
    'Capital Protection',
    'Instant Withdrawal',
    '24/7 Support',
    'Compounding Available',
    'Risk Management',
    'Auto Reinvest'
  ]

  const calculateTotalReturns = () => {
    const investment = pkg.investment || 1000
    const returnCap = pkg.returnCap || 3
    return (investment * returnCap).toLocaleString()
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:border-[#9E1C60] dark:group-hover:border-[#F5AD18] overflow-hidden h-full flex flex-col relative">
        
        {/* Level Badge */}
        <div className={`absolute top-4 right-4 ${levelBadge.color} px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg`}>
          {levelBadge.text}
        </div>

        {/* Header */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-r from-[#9E1C60] to-[#F5AD18] rounded-xl p-3 shadow-lg">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{pkg.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Investment Tier</p>
            </div>
          </div>
        </div>

        {/* Investment Details */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-2">
                <DollarSign className="w-4 h-4 text-[#9E1C60]" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Investment</span>
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                ${pkg.investment?.toLocaleString() || '1,000'}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-2">
                <TrendingUp className="w-4 h-4 text-[#F5AD18]" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Daily ROI</span>
              </div>
              <div className="text-lg font-bold text-[#9E1C60] dark:text-[#F5AD18]">
                {pkg.displayMinROI || 1.5}% - {pkg.displayMaxROI || 2.5}%
              </div>
            </div>
          </div>

          {/* Returns Summary */}
          <div className="bg-gradient-to-r from-[#561530]/5 to-[#9E1C60]/5 dark:from-[#561530]/20 dark:to-[#9E1C60]/20 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center">
                <div className="text-gray-600 dark:text-gray-400 mb-1">Total Returns</div>
                <div className="font-bold text-gray-900 dark:text-white">
                  ${calculateTotalReturns()}
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 dark:text-gray-400 mb-1">Min Withdrawal</div>
                <div className="font-bold text-[#9E1C60] dark:text-[#F5AD18]">
                  ${pkg.minWithdrawal || 10}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="p-6 flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-4 flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-[#F5AD18]" />
            <span>Key Features</span>
          </h4>
          <div className="space-y-3">
            {features.slice(0, 4).map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 bg-[#9E1C60] rounded-full flex-shrink-0"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="p-6 pt-4 mt-auto">
          <Link
            to="https://workplace.tgfuture.com/register"
            className="w-full bg-gradient-to-r from-[#561530] to-[#9E1C60] hover:from-[#811844] hover:to-[#9E1C60] text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center space-x-2 group/btn shadow-lg"
          >
            <span>Start Investing</span>
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Hover Accent */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#F5AD18] to-[#9E1C60] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>
    </motion.div>
  )
}

export default PackageCard